import React, { useState } from 'react';
import { MessageSquare, Send, CheckCircle } from 'lucide-react';
import { useAuth } from '../AuthContext';

const FeedbackPage: React.FC = () => {
    const { currentUser } = useAuth();
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!message.trim() || !currentUser) return;

        setIsSubmitting(true);
        setError(null);

        try {
            const token = await currentUser.getIdToken();
            const response = await fetch('/.netlify/functions/submit-feedback', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ message: message.trim() })
            });

            if (!response.ok) {
                throw new Error('Nepodařilo se odeslat zpětnou vazbu');
            }

            setIsSubmitted(true);
            setMessage('');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Něco se pokazilo');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSubmitted) {
        return (
            <div className="flex flex-col h-full min-h-0">
                <div className="flex-1 min-h-0 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
                        <div className="flex justify-center mb-6">
                            <div className="bg-green-100 dark:bg-green-900/30 rounded-full p-4">
                                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" strokeWidth={2} />
                            </div>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                            Děkujeme za zpětnou vazbu!
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-8">
                            Tvůj názor je pro nás důležitý a pomůže nám vylepšit aplikaci.
                        </p>
                        <button
                            onClick={() => setIsSubmitted(false)}
                            className="px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-2xl border-b-4 border-b-amber-600 active:border-b-0 active:translate-y-1 transition-all duration-150"
                        >
                            Odeslat další zpětnou vazbu
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full min-h-0">
            <div className="flex-1 min-h-0 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="max-w-2xl mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-center gap-3 mb-2">
                            <MessageSquare className="w-9 h-9 text-primary" strokeWidth={2.5} />
                            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
                                Zpětná vazba
                            </h1>
                        </div>
                        <p className="text-base text-gray-500 dark:text-gray-400 ml-[52px]">
                            Pomoz nám vylepšit aplikaci
                        </p>
                    </div>

                    {/* Feedback Form */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 sm:p-8">
                        <div className="mb-6">
                            <p className="text-gray-700 dark:text-gray-300">
                                Co se ti líbí? Co bys vylepšil/a? Narazil/a jsi na problém? Tvůj feedback nám pomůže vytvořit lepší produkt.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Napiš svůj feedback..."
                                rows={6}
                                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                            />

                            {error && (
                                <p className="mt-3 text-sm text-red-600 dark:text-red-400 font-medium">
                                    {error}
                                </p>
                            )}

                            <div className="mt-4 flex justify-end">
                                <button
                                    type="submit"
                                    disabled={!message.trim() || isSubmitting}
                                    className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-2xl border-b-4 border-b-amber-600 active:border-b-0 active:translate-y-1 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Send className="w-4 h-4" />
                                    {isSubmitting ? 'Odesílání...' : 'Odeslat'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeedbackPage;
